import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { 
  Shield, 
  Eye, 
  Camera, 
  Drone, 
  HardDrive,
  ArrowRight,
  CheckCircle,
  Code,
  Flame,
  Package,
  Cpu,
  ShieldCheck
} from 'lucide-react';

const ServicesPage = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const serviceCategories = [
    {
      title: "Cyber Security & Intelligence",
      description: "Advanced cybersecurity solutions and intelligence gathering for critical infrastructure protection and national security operations.",
      icon: Shield,
      image: "https://images.pexels.com/photos/5475750/pexels-photo-5475750.jpeg",
      services: [
        "Advanced Persistent Threat (APT) Detection",
        "Cyber Intelligence Gathering",
        "Vulnerability Assessment & Penetration Testing (VAPT)",
        "CDR IPDR Analysis for Government Agencies",
        "Network Security Monitoring",
        "Incident Response & Recovery"
      ],
      link: "/services/cyber-security",
      color: "from-blue-600 to-blue-800"
    },
    {
      title: "Counter-Intelligence & Surveillance",
      description: "Specialized equipment and services for counter-intelligence operations, surveillance detection, and security assessments.",
      icon: Eye,
      image: "https://images.pexels.com/photos/8728559/pexels-photo-8728559.jpeg",
      services: [
        "Advanced Spy Device Detection",
        "Counter-Surveillance Operations",
        "Technical Surveillance Countermeasures (TSCM)",
        "Room Debugging & Sweep Services",
        "Anti-Spy Device Installation",
        "Security Risk Assessments"
      ],
      link: "/services/counter-intelligence",
      color: "from-purple-600 to-purple-800"
    },
    {
      title: "Surveillance & Monitoring Systems",
      description: "Comprehensive surveillance solutions with advanced monitoring capabilities and networking infrastructure.",
      icon: Camera,
      image: "https://images.unsplash.com/photo-1589935447067-5531094415d1",
      services: [
        "Advanced CCTV Surveillance Systems",
        "IP-based Video Monitoring",
        "Video Door Phone Integration",
        "Network Infrastructure Setup",
        "Remote Monitoring Solutions",
        "Access Control Systems"
      ],
      link: "/services/surveillance",
      color: "from-green-600 to-green-800"
    },
    {
      title: "Drone Operations & Services",
      description: "Professional drone services with certified pilots for reconnaissance, surveillance, and intelligence missions.",
      icon: Drone,
      image: "https://images.pexels.com/photos/7663144/pexels-photo-7663144.jpeg",
      services: [
        "Aerial Reconnaissance Missions",
        "Surveillance & Monitoring Operations",
        "Certified Drone Pilot Services",
        "Tactical Flight Operations",
        "Real-time Intelligence Gathering",
        "Emergency Response Support"
      ],
      link: "/services/drone-operations",
      color: "from-red-600 to-red-800"
    },
    {
      title: "Digital Forensics & Recovery",
      description: "Expert digital forensics, data recovery solutions, custom software development, and emergency equipment.",
      icon: HardDrive,
      image: "https://images.unsplash.com/photo-1488590528505-98d2b5aba04b",
      services: [
        "Digital Evidence Recovery",
        "Computer Forensics Investigation",
        "Mobile Device Forensics",
        "Custom Software Development",
        "Data Recovery Services",
        "Emergency Response Equipment"
      ],
      link: "/services/digital-forensics",
      color: "from-yellow-600 to-yellow-800"
    },
    {
      title: "Software Development",
      description: "Customised, critical, and advanced software development with AI support, cloud-native architectures, and secure DevOps.",
      icon: Code,
      image: "https://images.unsplash.com/photo-1519389950473-47ba0277781c",
      services: [
        "Customised Critical Software",
        "AI-Powered Applications",
        "Cloud-Native & Edge Solutions",
        "Secure DevOps & Automation",
        "Agile Teams & Collaboration",
        "Legacy System Modernization"
      ],
      link: "/services/software-development",
      color: "from-blue-600 to-blue-800"
    },
    {
      title: "Fire Fighting System",
      description: "Comprehensive fire detection, suppression, and emergency response solutions for industrial, commercial, and residential safety.",
      icon: Flame,
      image: "https://images.unsplash.com/photo-1509228468518-180dd4864904",
      services: [
        "Fire Detection Systems",
        "Fire Suppression Systems",
        "Fire Extinguishers",
        "Fire Alarm Systems",
        "Wet/Dry/Deluge/CO2 Systems",
        "Operation: Detection, Alarm, Suppression, Monitoring"
      ],
      link: "/services/fire-fighting-system",
      color: "from-red-600 to-red-800"
    },
    {
      title: "Warehouse Management",
      description: "Advanced RFID-based inventory tracking, automated forklift operations, and intelligent warehouse management solutions for optimized logistics and supply chain operations.",
      icon: Package,
      image: "https://images.unsplash.com/photo-1689942010216-dc412bb1e7a9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHx3YXJlaG91c2UlMjBhdXRvbWF0aW9ufGVufDB8fHx8MTc1MjI0ODI0MXww&ixlib=rb-4.1.0&q=85",
      services: [
        "RFID Tracking Systems",
        "Automated Forklift Operations",
        "Inventory Management",
        "Warehouse Automation",
        "Supply Chain Optimization",
        "Real-time Monitoring"
      ],
      link: "/services/warehouse-management",
      color: "from-green-600 to-green-800"
    },
    {
      title: "Research & Development",
      description: "Custom embedded systems, IoT solutions, and specialized spy device development with cutting-edge electronics and software integration for intelligence and security applications.",
      icon: Cpu,
      image: "https://images.unsplash.com/photo-1650530415027-dc9199f473ec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxlbGVjdHJvbmljcyUyMGRldmVsb3BtZW50fGVufDB8fHx8MTc1MjI0ODI0Nnww&ixlib=rb-4.1.0&q=85",
      services: [
        "Embedded Systems Development",
        "IoT Solutions",
        "Spy Device Development",
        "Custom Electronics",
        "Hardware Integration",
        "Prototype Development"
      ],
      link: "/services/research-development",
      color: "from-purple-600 to-purple-800"
    },
    {
      title: "Safety Equipment",
      description: "Comprehensive physical security solutions including access control systems, perimeter security, and advanced barrier technologies for critical infrastructure protection.",
      icon: ShieldCheck,
      image: "https://images.unsplash.com/photo-1569784210445-990e7e44ccfe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxzZWN1cml0eSUyMGJhcnJpZXJzfGVufDB8fHx8MTc1MjI0ODI5MXww&ixlib=rb-4.1.0&q=85",
      services: [
        "Boom Barriers",
        "Access Control Systems",
        "Turnstiles",
        "Perimeter Security",
        "Security Barriers",
        "Physical Protection"
      ],
      link: "/services/safety-equipment",
      color: "from-orange-600 to-orange-800"
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-r from-rich-burgundy/20 to-near-black relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-20" />
        <div className="container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="heading-font text-5xl md:text-7xl font-bold mb-6">
              Our <span className="text-royal-gold">Services</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
              Comprehensive cybersecurity, intelligence, and digital forensics solutions 
              tailored for government agencies and enterprise clients worldwide.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="section-padding bg-gray-dark" ref={ref}>
        <div className="container">
          <div className="space-y-20">
            {serviceCategories.map((category, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                }`}
              >
                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="flex items-center space-x-4 mb-6">
                    <div className={`w-16 h-16 bg-gradient-to-br ${category.color} rounded-16 flex items-center justify-center`}>
                      <category.icon className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h2 className="heading-font text-3xl md:text-4xl font-bold text-off-white">
                        {category.title}
                      </h2>
                    </div>
                  </div>
                  
                  <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                    {category.description}
                  </p>
                  
                  {/* Service List */}
                  <div className="space-y-3 mb-8">
                    {category.services.map((service, serviceIndex) => (
                      <div key={serviceIndex} className="flex items-center space-x-3">
                        <CheckCircle className="w-5 h-5 text-royal-gold flex-shrink-0" />
                        <span className="text-gray-200">{service}</span>
                      </div>
                    ))}
                  </div>
                  
                  <Link
                    to={category.link}
                    className="inline-flex items-center space-x-2 cta-button group"
                  >
                    <span>Learn More</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                  </Link>
                </div>

                {/* Image */}
                <div className={index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}>
                  <div className="relative rounded-24 overflow-hidden group">
                    <img 
                      src={category.image} 
                      alt={category.title}
                      className="w-full h-80 object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-near-black/60 to-transparent" />
                    <div className="absolute bottom-6 left-6">
                      <div className={`w-12 h-12 bg-gradient-to-br ${category.color} rounded-12 flex items-center justify-center`}>
                        <category.icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section
      <section className="section-padding bg-near-black">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="glass-morphism rounded-24 p-12 max-w-4xl mx-auto">
              <h2 className="heading-font text-4xl md:text-5xl font-bold mb-6 text-off-white">
                Ready to Secure Your Operations?
              </h2>
              <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                Contact our expert team for a confidential consultation and discover how 
                CIARL can enhance your security posture with cutting-edge solutions.
              </p>
              <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6">
                <Link to="/#contact" className="cta-button">
                  Schedule Consultation
                </Link>
                <Link to="/government-solutions" className="secondary-button">
                  Government Programs
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section> */}
    </div>
  );
};

export default ServicesPage;